import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from '../../context/FormContext';
import StepLayout from '../StepLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ThankYou from '../ThankYou';
// import HCaptcha from '@hcaptcha/react-hcaptcha';

const { FiCheck, FiAlertCircle, FiChevronLeft } = FiIcons;

/* Helpers */
const normaliseHex = (hex) => {
  if (typeof hex !== 'string') return null;
  let h = hex.trim();
  if (!h) return null;
  if (h[0] !== '#') h = `#${h}`;
  if (h.length === 4) h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  return /^#([0-9A-Fa-f]{6})$/.test(h) ? h : null;
};
const hexToRgb = (hex) => {
  const h = normaliseHex(hex);
  if (!h) return null;
  const n = parseInt(h.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const isDark = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq < 140;
};
const textColourFor = (hex) => (isDark(hex) ? '#FFFFFF' : '#000000');
const numberToWords = (n) => {
  const a = ['zero','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
  const b = ['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];
  if (n < 20) return a[n];
  if (n < 100) return b[Math.floor(n/10)] + (n%10 ? '-' + a[n%10] : '');
  return String(n);
};

function Step7ReviewSubmission() {
  const { formData, updateFormData, resetForm, validateStep } = useForm();
  const stepData = formData.step7 || {};
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [step1Errors, setStep1Errors] = useState([]);
  const [showStep1ErrorBar, setShowStep1ErrorBar] = useState(false);
  // const hcaptchaRef = useRef(null);

  //  const HCAPTCHA_SITE_KEY = '9a24ea70-25a4-4e0e-9668-a332ddb48b0c';

  const handleInputChange = (field, value) => updateFormData('step7', { [field]: value });

  const handleEdit = (stepNumber) => navigate(`/step/${stepNumber}`);

  const validateAllSteps = () => {
    const allErrors = {};
    for (let i = 1; i <= 7; i++) {
      const stepErrors = validateStep(i);
      if (Object.keys(stepErrors).length > 0) allErrors[`step${i}`] = stepErrors;
    }
    return allErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allErrors = validateAllSteps();
    const missingStep1 = [];
if (!formData.step1.businessName) missingStep1.push("Business name");
if (!formData.step1.contactName) missingStep1.push("Contact person’s name");
if (!formData.step1.email) missingStep1.push("Email address");

if (missingStep1.length > 0) {
  setStep1Errors(missingStep1);
  setShowStep1ErrorBar(true);
  setTimeout(() => setShowStep1ErrorBar(false), 20000);
  setTimeout(() => {
    const el = document.getElementById("step1-error-bar");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 100);
  return;
}

    setIsSubmitting(true);

    try {
      // Build FormData
      const formDataObj = new FormData();

      // Append form fields (flatten nested objects)
      Object.entries(formData).forEach(([stepKey, stepValues]) => {
        Object.entries(stepValues).forEach(([field, value]) => {
          if (value != null) {
            if (Array.isArray(value)) {
              value.forEach((v) => formDataObj.append(`${stepKey}[${field}][]`, v));
            } else if (typeof value === 'object' && value !== null && field === 'pageContent') {
              // Specially handle pageContent, flatten each page
              Object.entries(value).forEach(([k, v]) => {
                formDataObj.append(`${stepKey}[${field}][${k}]`, v);
              });
            } else if (typeof value === 'object') {
              formDataObj.append(`${stepKey}[${field}]`, JSON.stringify(value));
            } else {
              formDataObj.append(`${stepKey}[${field}]`, value);
            }
          }
        });
      });

      // File inputs if any
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input) => {
        Array.from(input.files).forEach((file) => {
          formDataObj.append('attachments[]', file);
        });
      });

      // hCaptcha (invisible)
/*let token = '';
try {
  if (hcaptchaRef.current && typeof hcaptchaRef.current.execute === 'function') {
    token = await hcaptchaRef.current.execute();
  } else {
    throw new Error("hCaptcha not ready");
  }
  if (hcaptchaRef.current && typeof hcaptchaRef.current.reset === 'function') {
    hcaptchaRef.current.reset();
  }
  formDataObj.append('h-captcha-response', token);
} catch (_) {
  setSubmitStatus('error');
  alert('CAPTCHA validation failed.');
  setIsSubmitting(false);
  return;
}
*/
      // Submit to PHP handler
      const response = await fetch('https://forms.primehost.co.za/forms/form-handler.php', {
        method: 'POST',
        body: formDataObj
      });

      const result = await response.json();

      if (result.status === 'success') {
        setSubmitStatus('success');
        resetForm();
      } else {
        setSubmitStatus('error');
        alert(result.message || 'Submission failed');
      }

    } catch (err) {
      console.error(err);
      setSubmitStatus('error');
      alert('Submission failed, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* Reusable label-value row with aligned values column */
  const LabelValue = ({ label, children }) => (
    <div className="text-sm md:text-base leading-relaxed grid grid-cols-[180px_minmax(0,1fr)] items-start gap-x-4">
      <span className="font-semibold">{label}</span>
      <span className="text-[#6a717c]">{children || 'Not specified'}</span>
    </div>
  );

  /* Section wrapper: 15% opacity border + thin divider line */
  const brandBorder = '1px solid rgba(255,88,65,0.15)';
  const brandDividerStyle = { height: '1px', backgroundColor: 'rgba(255,88,65,0.15)', width: '100%' };

  const Section = ({ id, title, onEdit, children }) => (
    <section
      aria-labelledby={id}
      className="animate-slideDown rounded-xl review-section"
      style={{ border: brandBorder }}
    >
      <div className="flex items-center justify-between">
        <h4 id={id} className="text-lg font-semibold text-gray-900">{title}</h4>
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${title}`}
          className="inline-flex items-center gap-1.5 text-[#FF5841] border border-transparent rounded-md px-2 py-1 hover:bg-[#FFEEEC] hover:border-[#ffd1c9] hover:text-[#E5503A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5841] focus-visible:ring-offset-2"
        >
          <span aria-hidden="true" className="text-base">✏️</span>
          <span className="text-sm md:text-base">Edit</span>
        </button>
      </div>
      <div className="mt-1 mb-4" style={brandDividerStyle} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {children}
      </div>
    </section>
  );

  /* ✍️ Website content — special layout adopted exactly as per your old version */
  const WebsiteContentSection = () => {
    const primary = normaliseHex(formData.step2?.primaryColour) || '#FF5841';
    const textColour = primary.toLowerCase() === '#ff5841' ? '#FFFFFF' : textColourFor(primary);
    const businessName = (formData.step1?.businessName || 'YOURCOMPANY').split(' ')[0].toUpperCase();
    const selectedPages = Array.isArray(formData.step4?.selectedPages) ? formData.step4.selectedPages : [];
    const cap = (s) => (typeof s === 'string' && s.length ? s[0].toUpperCase() + s.slice(1) : s);
    const items = selectedPages.slice(0, 5).map((x) => cap(x));
    if (selectedPages.length > 5) items.push('…');
    const pageContent = formData.step4?.pageContent || {};
    const count = Object.keys(pageContent).length || 0;

    return (
      <section
        aria-labelledby="section-step-4"
        className="animate-slideDown rounded-xl review-section"
        style={{ border: brandBorder }}
      >
        <div className="flex items-center justify-between">
          <h4 id="section-step-4" className="text-lg font-semibold text-gray-900">✍️ Website content</h4>
          <button
            type="button"
            onClick={() => handleEdit(4)}
            aria-label="Edit website content"
            className="inline-flex items-center gap-1.5 text-[#FF5841] border border-transparent rounded-md px-2 py-1 hover:bg-[#FFEEEC] hover:border-[#ffd1c9] hover:text-[#E5503A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5841] focus-visible:ring-offset-2"
          >
            <span aria-hidden="true" className="text-base">✏️</span>
            <span className="text-sm md:text-base">Edit</span>
          </button>
        </div>
        <div className="mt-1 mb-4" style={brandDividerStyle} />
        {/* Mock nav bar */}
        <div className="rounded-md mb-4" style={{ backgroundColor: primary, color: textColour }}>
          <div className="px-4 py-2 flex items-center justify-between">
            <div className="font-bold tracking-wide" style={{ color: '#FFFFFF' }}>
              {businessName}
            </div>
            <nav className="text-sm md:text-base font-medium">
              {items.length > 0 ? (
                <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-end">
                  {items.map((item, i) => (
  <li key={i} className="truncate" style={{ color: '#FFFFFF' }}>{item}</li>
))}
                </ul>
              ) : (
                <span style={{ color: '#FFFFFF' }}>Select pages for your website in Step 4</span>
              )}
            </nav>
          </div>
        </div>
        {/* Hero blurb: bold + 18px; number in words coloured with primary */}
        <div>
          {count > 0 ? (
            <p className="font-bold" style={{ fontSize: '18px' }}>
              <p className="font-bold" style={{ fontSize: '18px' }}>
  Content provided for <span style={{ color: primary }}>
    {numberToWords(count)} {count === 1 ? 'page' : 'pages'}
  </span>
</p>
            </p>
          ) : (
            <p className="font-bold" style={{ fontSize: '18px' }}>
              Content not provided, <span style={{ color: primary }}>but we have you covered</span>
            </p>
          )}
        </div>
      </section>
    );
  };

  /* ------------ RENDER -------------*/

  if (submitStatus === 'success') {
  return <ThankYou />;
}

  return (
    <StepLayout
      title="Almost there – let's make sure everything's perfect! 🎉"
      stepNumber={7}
      totalSteps={7}
    >

      <div className="p-4 bg-[#E5F9F9] border-l-4 border-[#008080] rounded-lg mb-6">
  <p className="text-[#008080] text-base">
    You have done brilliantly getting this far! Take your time to review each section, and once you are happy, we will take the reins and start bringing your vision to life.
  </p>
  <p className="text-[#008080] text-base italic mt-2">
    Don't worry if your progress bar is not full, you can still submit; we will top it up for you.
  </p>
</div>

    
      {/* Increased spacing between sections: space-y-10 */}
      <form className="space-y-10" onSubmit={handleSubmit} encType="multipart/form-data">
        <Section id="section-step-1" title="🏢 Business details" onEdit={() => handleEdit(1)}>
          <LabelValue label="Business:">{formData.step1?.businessName}</LabelValue>
          <LabelValue label="Contact:">
            {(formData.step1?.contactName || '') + (formData.step1?.email ? ` (${formData.step1.email})` : '') || null}
          </LabelValue>
          <LabelValue label="Industry:">
            {formData.step1?.industry ? formData.step1.industry.replace(/-/g, ' ') : null}
          </LabelValue>
          <LabelValue label="Website type:">
            {formData.step1?.websiteType === 'new'
              ? 'New website'
              : formData.step1?.websiteType === 'refresh'
                ? 'Refresh existing'
                : null}
          </LabelValue>
        </Section>

        <Section id="section-step-2" title="🎨 Design preferences" onEdit={() => handleEdit(2)}>
          <LabelValue label="Brand colours:">
            {(() => {
              const primary = formData.step2?.primaryColour;
              const secondary = formData.step2?.secondaryColour;
              if (!primary && !secondary) return 'Not specified';
              return (
                <span className="inline-flex flex-wrap items-center gap-4">
                  {primary && (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 rounded-sm" style={{ backgroundColor: normaliseHex(primary) || primary }} />
                      <span className="font-mono">{primary}</span>
                    </span>
                  )}
                  {secondary && (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 rounded-sm" style={{ backgroundColor: normaliseHex(secondary) || secondary }} />
                      <span className="font-mono">{secondary}</span>
                    </span>
                  )}
                </span>
              );
            })()}
          </LabelValue>
          <LabelValue label="Typography:">{formData.step2?.selectedFont || 'Not selected'}</LabelValue>
          <LabelValue label="Design style:">
            {(formData.step2?.designStyle && formData.step2.designStyle.join(', ')) || 'Not specified'}
          </LabelValue>
        </Section>

        <Section id="section-step-3" title="🖼️ Brand assets" onEdit={() => handleEdit(3)}>
          <LabelValue label="Logo status:">
            {formData.step3?.logoStatus ? formData.step3.logoStatus.replace(/-/g, ' ') : 'Not specified'}
          </LabelValue>
          <LabelValue label="Brand assets:">
            {(formData.step3?.brandAssets && formData.step3.brandAssets.join(', ')) || 'None specified'}
          </LabelValue>
        </Section>

        <WebsiteContentSection />

        <Section id="section-step-5" title="👥 Target audience & goals" onEdit={() => handleEdit(5)}>
          <LabelValue label="Age range:">
            {(formData.step5?.primaryAgeRange && formData.step5.primaryAgeRange.join(', ')) || 'Not specified'}
          </LabelValue>
          <LabelValue label="Geographic focus:">
            {(formData.step5?.geographicFocus && formData.step5.geographicFocus.join(', ')) || 'Not specified'}
          </LabelValue>
          <LabelValue label="Customer type:">
            {formData.step5?.customerType ? String(formData.step5.customerType) : 'Not specified'}
          </LabelValue>
        </Section>

        <Section id="section-step-6" title="⚙️ Technical requirements" onEdit={() => handleEdit(6)}>
          <LabelValue label="Domain:">
            {formData.step6?.domainOwnership === 'Yes'
              ? 'Existing domain'
              : formData.step6?.domainOwnership === 'No'
                ? 'New domain needed'
                : 'Not specified'}
          </LabelValue>
          <LabelValue label="Hosting:">
            {formData.step6?.hostingChoice ? formData.step6.hostingChoice.replace(/-/g, ' ') : 'Not specified'}
          </LabelValue>
          <LabelValue label="Email hosting:">
            {formData.step6?.emailHosting === 'Yes'
              ? 'Required'
              : formData.step6?.emailHosting === 'Existing'
                ? 'Not required'
                : 'Not specified'}
          </LabelValue>
        </Section>

        {/* Agreements */}
        <div className="border-t border-gray-200 pt-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="terms"
                checked={!!stepData.termsAccepted}
                onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                className="agreement-checkbox mt-1"
              />
              <label htmlFor="terms" className="text-16px text-gray-700">
                I agree to the <a href="https://webdesignspecs.primehost.co.za/#/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-[#FF5841] hover:underline">Terms & Conditions</a> <span className="mandatory-asterisk">*</span>
              </label>
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="confirmation"
                checked={!!stepData.confirmationAccepted}
                onChange={(e) => handleInputChange('confirmationAccepted', e.target.checked)}
                className="agreement-checkbox mt-1"
              />
              <label htmlFor="confirmation" className="text-16px text-gray-700">
                I confirm that the information I have provided is accurate and complete, and I understand that, upon submission, all form content will be deleted and the form will be reset. <span className="mandatory-asterisk">*</span>
              </label>
            </div>
          </div>
          <p className="text-15px text-gray-500 mt-4">
            All details submitted will be used solely for project development and communication purposes.
          </p>
        </div>

        {/* hCaptcha */}
        {/* <HCaptcha
          ref={hcaptchaRef}
          sitekey={HCAPTCHA_SITE_KEY}
          size="invisible"
        /> */}

        {/* Single Previous + Submit */}
        {showStep1ErrorBar && step1Errors.length > 0 && (
  <div
    id="step1-error-bar"
    className="bg-[#FFEFEB] border-l-4 border-[#FF5841] text-[#ED4346] rounded-lg px-6 py-4 mb-6 font-medium shadow"
    style={{ marginTop: 24 }}
  >
    <div className="mb-2">
      Please complete the following required fields in <b>Step 1</b>:
    </div>
    <ul className="list-disc list-inside text-base ml-3">
      {step1Errors.map((err, idx) => (
        <li key={idx}>{err}</li>
      ))}
    </ul>
  </div>
)}

        <div className="border-t border-gray-200 pt-6">
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => navigate('/step/6')}
              className="flex items-center space-x-2 px-6 py-3 border-2 border-[#FF5841] text-[#FF5841] rounded-lg hover:bg-[#FF5841] hover:text-white transition-colors duration-200 font-medium"
            >
              <SafeIcon icon={FiChevronLeft} className="h-4 w-4" />
              <span className="font-medium">Previous</span>
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !stepData.termsAccepted || !stepData.confirmationAccepted}
              className={`px-8 py-3 rounded-lg font-medium transition-colors duration-200 ${
                isSubmitting || !stepData.termsAccepted || !stepData.confirmationAccepted
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#008080] text-white hover:bg-[#006666]'
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit project >'}
            </button>
          </div>
        </div>

        {submitStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-500" />
              <p className="text-red-700">
                There was an error submitting your form. Please try again or contact support.
              </p>
            </div>
          </div>
        )}
      </form>
    </StepLayout>
  );
}

export default Step7ReviewSubmission;